import React, { Component } from "react";
import { Modal } from "semantic-ui-react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";
import { isLoggedIn } from "../../utils/session";
import { AdminTeam } from "../../stores/ProfileStore/Types";
import Teams from "./Teams";

interface TeamsModalProps {
  teams: AdminTeam[];
  showTeams: boolean;
  handleShowTeams: (e: any, val: boolean) => void;
}

interface Props {
  adminUser: boolean;
  qaUser: boolean;
  superUser: boolean;
}

interface State {
  teams: AdminTeam[];
  showTeams: boolean;
  loading: boolean;
}

const TeamsModal: React.FC<TeamsModalProps> = ({
  showTeams,
  handleShowTeams,
  teams,
}) => {
  return (
    <Modal
      size="tiny"
      open={showTeams}
      onClose={(e) => handleShowTeams(e, false)}
      className="teams-modal"
      closeOnDimmerClick
      aria-labelledby="teams-title"
    >
      <div className="teams-modal-head">
        <h2 id="teams-title">Your teams</h2>
        <button
          type="button"
          className="teams-close"
          aria-label="Close"
          onClick={(e) => handleShowTeams(e, false)}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M4 4 L12 12 M12 4 L4 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <p className="teams-sub">Pick a team to open its admin page.</p>
      <Teams teams={teams} />
    </Modal>
  );
};

class AdminLinks extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      teams: [],
      showTeams: false,
      loading: true,
    };
  }

  componentDidMount() {
    // Team admin lists only exist for a signed-in user; signed out this returns [].
    if (!isLoggedIn()) {
      this.setState({ loading: false });
      return;
    }

    return getRequest("/api/users/teams.json").then(
      (response: AxiosResponse<AdminTeam[]>) => {
        this.setState({ teams: response.data, loading: false });
      },
    );
  }

  handleShowTeams = (e, val) => {
    e.preventDefault();

    this.setState({ showTeams: val });
  };

  render() {
    const { adminUser, qaUser, superUser } = this.props;
    const { teams, showTeams, loading } = this.state;
    const hasLinks = adminUser || qaUser || superUser || teams?.length > 0;

    if (!loading && !hasLinks) {
      return null;
    }

    return (
      <div className="pf-admin">
        {adminUser && <div className="pf-section-label">Admin tools</div>}
        {loading && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}
        {adminUser && (
          <a href="/admin">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M8 1.5 L13 3.5 V8 c 0 3.2 -2.2 5.5 -5 6.5 c -2.8 -1 -5 -3.3 -5 -6.5 V3.5 z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              ></path>
              <path
                d="M6.2 8 L7.5 9.3 L10 6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
            Admin panel
          </a>
        )}
        {superUser && (
          <a href="/admin/impersonation" data-method="delete">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M3 6.2 H13 L11.8 3.5 H4.2 Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path
                d="M5.2 6.2 L4.4 8.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
              <path
                d="M10.8 6.2 L11.6 8.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
              <circle
                cx="5.6"
                cy="10"
                r="1.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <circle
                cx="10.4"
                cy="10"
                r="1.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <path
                d="M7.4 10 H8.6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            Authentic
          </a>
        )}
        {teams?.length > 0 && teams?.length < 2 && (
          <a href={teams[0].link}>
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <circle
                cx="5.5"
                cy="6"
                r="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              ></circle>
              <circle
                cx="10.5"
                cy="6"
                r="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              ></circle>
              <path
                d="M1.5 13.5 c 0 -2 1.8 -3.5 4 -3.5 s 4 1.5 4 3.5 M6.5 13.5 c 0 -2 1.8 -3.5 4 -3.5 s 4 1.5 4 3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              ></path>
            </svg>
            <span>{teams[0].name}</span>
          </a>
        )}
        {teams?.length > 1 && (
          <a onClick={(e) => this.handleShowTeams(e, true)}>
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <circle
                cx="5.5"
                cy="6"
                r="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              ></circle>
              <circle
                cx="10.5"
                cy="6"
                r="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              ></circle>
              <path
                d="M1.5 13.5 c 0 -2 1.8 -3.5 4 -3.5 s 4 1.5 4 3.5 M6.5 13.5 c 0 -2 1.8 -3.5 4 -3.5 s 4 1.5 4 3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              ></path>
            </svg>
            <span>Teams</span>
          </a>
        )}
        {teams?.length > 1 && (
          <TeamsModal
            showTeams={showTeams}
            handleShowTeams={this.handleShowTeams}
            teams={teams}
          />
        )}
      </div>
    );
  }
}

export default AdminLinks;
